import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Slider,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { PackagingSolution, Product, BRAND_COLORS, FONTS } from '../types/index.ts';
import BoxVisualization from './BoxVisualization.tsx';
import FillLine from './FillLine.tsx';
import FillCube from './FillCube.tsx';
import DensityRail from './DensityRail.tsx';
import { encodeShareData } from '../services/shareCodec.ts';

interface ResultsDisplayProps {
  product: Product | Product[];
  solutions: PackagingSolution[];
}

const ResultsDisplay: React.FC<ResultsDisplayProps> = ({ product: productProp, solutions }) => {
  // Conversion en tableau si c'est un produit unique (identité stable pour les hooks)
  const products = useMemo(
    () => (Array.isArray(productProp) ? productProp : [productProp]),
    [productProp]
  );

  const [expandedSolution, setExpandedSolution] = useState<string | null>(
    solutions.length > 0 ? solutions[0].boxId : null
  );

  // Filter states
  const [maxBoxesFilter, setMaxBoxesFilter] = useState<number>(
    Math.max(...solutions.map(s => s.totalBoxesNeeded)) + 5
  );
  const [minEfficiencyFilter, setMinEfficiencyFilter] = useState<number>(0);
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(false);

  // État pour les solutions sélectionnées pour le partage
  const [selectedSolutions, setSelectedSolutions] = useState<{ [key: string]: boolean }>({});
  const [shareDialogOpen, setShareDialogOpen] = useState<boolean>(false);
  const [shareLink, setShareLink] = useState<string>('');
  const [showShareTooltip, setShowShareTooltip] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  // État pour indiquer quand le lien est mis à jour
  const [linkJustUpdated, setLinkJustUpdated] = useState<boolean>(false);
  // État pour stocker les prix des boîtes
  const [boxPrices, setBoxPrices] = useState<{ [key: string]: string }>({});

  // Calculate actual space utilization based on requested quantity
  const calculateActualSpaceUtilization = useCallback((solution: PackagingSolution): number => {
    const quantity =
      solution.products && solution.products.length > 1
        ? solution.totalProductsQuantity || products.reduce((sum, p) => sum + p.quantity, 0)
        : products[0].quantity;
    const fullBoxCount = Math.floor(quantity / solution.productsPerBox);
    const remainingProducts = quantity % solution.productsPerBox;
    const totalActualProducts = fullBoxCount * solution.productsPerBox + remainingProducts;
    const maxPossibleProducts = solution.totalBoxesNeeded * solution.productsPerBox;
    return (totalActualProducts / maxPossibleProducts) * solution.spaceUtilization;
  }, [products]);

  // Filtrage + tri affiché : « minimum de caisses d'abord, puis utilisation »,
  // exactement ce que le texte d'introduction promet (et ce que fait le calculateur).
  const filteredSolutions = useMemo(() => {
    return solutions
      .filter(
        (solution) =>
          solution.totalBoxesNeeded <= maxBoxesFilter &&
          calculateActualSpaceUtilization(solution) >= minEfficiencyFilter &&
          // Garde-fou : une utilisation > 100 % n'est pas réalisable.
          calculateActualSpaceUtilization(solution) <= 100
      )
      .sort(
        (a, b) =>
          a.totalBoxesNeeded - b.totalBoxesNeeded ||
          calculateActualSpaceUtilization(b) - calculateActualSpaceUtilization(a)
      );
  }, [solutions, maxBoxesFilter, minEfficiencyFilter, calculateActualSpaceUtilization]);

  // Nouvelle recherche : recaler la borne du slider, déplier et présélectionner
  // la meilleure solution. (Ne PAS dépendre des filtres : bouger un slider ne
  // doit jamais écraser la sélection de partage de l'utilisateur.)
  useEffect(() => {
    if (solutions.length === 0) return;
    setMaxBoxesFilter(Math.max(...solutions.map(s => s.totalBoxesNeeded)) + 5);
    const best = [...solutions].sort(
      (a, b) =>
        a.totalBoxesNeeded - b.totalBoxesNeeded ||
        calculateActualSpaceUtilization(b) - calculateActualSpaceUtilization(a)
    )[0];
    setExpandedSolution(best.boxId);
    setSelectedSolutions({ [best.boxId]: true });
  }, [solutions, calculateActualSpaceUtilization]);

  // Si les filtres cachent la carte dépliée, déplier la première visible
  // (sans toucher à la sélection de partage). On ne ré-ouvre QUE lorsqu'une carte
  // était dépliée puis a été masquée par un filtre — pas quand l'utilisateur l'a
  // volontairement repliée (expandedSolution === null), sinon la carte se rouvre
  // toute seule et il devient impossible de tout replier.
  useEffect(() => {
    if (filteredSolutions.length === 0) return;
    if (expandedSolution !== null && !filteredSolutions.some((s) => s.boxId === expandedSolution)) {
      setExpandedSolution(filteredSolutions[0].boxId);
    }
  }, [filteredSolutions, expandedSolution]);

  // Mettre à jour automatiquement le lien de partage quand les prix ou les sélections changent
  useEffect(() => {
    // Ne mettre à jour que si au moins une solution est sélectionnée
    const hasSelectedSolutions = Object.values(selectedSolutions).some(selected => selected);
    if (hasSelectedSolutions) {
      updateShareLink();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxPrices, selectedSolutions, solutions, productProp]);
  
  // Fonction pour mettre à jour le lien de partage sans ouvrir la boîte de dialogue
  const updateShareLink = () => {
    // Filtrer les solutions sélectionnées
    const solutionsToShare = solutions.filter(solution => selectedSolutions[solution.boxId]);
    
    if (solutionsToShare.length === 0) {
      return;
    }
    
    // Ajouter les prix aux solutions (si spécifiés)
    const solutionsWithPrices = solutionsToShare.map(solution => {
      // Si un prix a été défini pour cette boîte, l'inclure
      if (boxPrices[solution.boxId] && boxPrices[solution.boxId].trim() !== '') {
        return {
          ...solution,
          price: boxPrices[solution.boxId]
        };
      }
      return solution;
    });
    
    // Créer l'objet à partager
    const shareData = {
      products: productProp,
      solutions: solutionsWithPrices
    };

    // Encodage URL-safe supportant les accents (btoa nu jette sur é, €, …)
    const baseUrl = window.location.origin;
    let shareUrl = `${baseUrl}/shared?shared=${encodeShareData(shareData)}`;

    // Garde-fou : au-delà de ~30 Ko, nginx répond 414 au destinataire
    // (large_client_header_buffers 8 32k). On retire alors les placements 3D
    // du lien — la page partagée retombe sur sa grille de secours.
    const MAX_SHARE_URL_LENGTH = 30000;
    if (shareUrl.length > MAX_SHARE_URL_LENGTH) {
      const slimSolutions = solutionsWithPrices.map(
        ({ productPlacements, boxInstances, ...rest }) => rest
      );
      shareUrl = `${baseUrl}/shared?shared=${encodeShareData({ products: productProp, solutions: slimSolutions })}`;
    }

    setShareLink(shareUrl);
    
    // Activer l'indicateur de mise à jour
    setLinkJustUpdated(true);
    // Désactiver après 1,5 secondes
    setTimeout(() => setLinkJustUpdated(false), 1500);
  };
  
  // Fonction pour générer un lien de partage et ouvrir la boîte de dialogue
  const generateShareLink = () => {
    // Filtrer les solutions sélectionnées
    const solutionsToShare = solutions.filter(solution => selectedSolutions[solution.boxId]);
    
    if (solutionsToShare.length === 0) {
      setSnackbarOpen(true);
      return;
    }
    
    // Utilisez la fonction updateShareLink pour mettre à jour le lien
    updateShareLink();
    
    // Ouvrir la boîte de dialogue
    setShareDialogOpen(true);
  };
  
  // Fonction pour gérer le changement de prix d'une boîte
  const handlePriceChange = (boxId: string, price: string) => {
    setBoxPrices(prev => ({
      ...prev,
      [boxId]: price
    }));
    // La mise à jour du lien se fera automatiquement via l'effet
  };
  
  // Fonction pour gérer la sélection/désélection d'une solution
  const handleSelectSolution = (boxId: string) => {
    setSelectedSolutions(prev => ({
      ...prev,
      [boxId]: !prev[boxId]
    }));
  };
  
  // Fonction pour copier le lien dans le presse-papier (avec repli si l'API
  // clipboard est absente ou refusée — la copie ne doit jamais échouer en silence)
  const copyShareLink = () => {
    const onCopied = () => {
      setShowShareTooltip(true);
      setTimeout(() => setShowShareTooltip(false), 1500);
    };
    const fallbackCopy = () => {
      const input = document.getElementById('share-link') as HTMLInputElement | null;
      if (!input) return;
      input.focus();
      input.select();
      try {
        if (document.execCommand('copy')) onCopied();
      } catch {
        /* le texte reste sélectionné : l'utilisateur peut copier manuellement */
      }
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(shareLink).then(onCopied).catch(fallbackCopy);
    } else {
      fallbackCopy();
    }
  };
  
  // Fermer la boîte de dialogue de partage
  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
  };
  
  // Fermer le snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (solutions.length === 0) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          mb: 4,
          borderRadius: 2,
          backgroundColor: '#FFF9E6',
          borderLeft: `4px solid #FFC107`,
        }}
      >
        <Typography variant="h6" color="warning.dark" gutterBottom>
          No solution found
        </Typography>
        <Typography variant="body1">
          We couldn't find a box suitable for your dimensions.
          Try modifying the product dimensions or adding new boxes to your database.
        </Typography>
      </Paper>
    );
  }

  const handleExpandSolution = (boxId: string) => {
    setExpandedSolution(expandedSolution === boxId ? null : boxId);
  };

  const getOrientationLabel = (orientation: string) => {
    switch (orientation) {
      case 'standard':
        return 'Standard';
      case 'tilted':
        return 'Tilted';
      case 'length-rotated':
        return 'Length Rotated';
      case 'width-rotated':
        return 'Width Rotated';
      case 'flipped':
        return 'Flipped';
      case 'combined-rotation':
        return 'Combined Rotation';
      default:
        return orientation;
    }
  };

  const getOrientationColor = (orientation: string) => {
    switch (orientation) {
      case 'standard':
        return BRAND_COLORS.primary;
      case 'tilted':
        return BRAND_COLORS.secondary;
      case 'length-rotated':
        return '#9C27B0'; // Purple color
      case 'width-rotated':
        return '#2196F3'; // Blue color
      case 'flipped':
        return '#FF5722'; // Orange color
      case 'combined-rotation':
        return '#00BCD4'; // Cyan color
      default:
        return 'default';
    }
  };

  return (
    <Box
      sx={{
        mt: 4,
        mb: 6,
        animation: 'fadeIn 0.6s ease-in-out',
        '@keyframes fadeIn': {
          '0%': { opacity: 0, transform: 'translateY(20px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' }
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" gutterBottom sx={{ color: BRAND_COLORS.primary, fontWeight: 'bold', mb: 0 }}>
          Recommended Packaging Solutions
      </Typography>
        
        <Button 
          startIcon={<FilterListIcon />} 
          onClick={() => setFiltersExpanded(!filtersExpanded)}
          variant={filtersExpanded ? "contained" : "outlined"}
          size="small"
          sx={{ 
            mb: 1, 
            backgroundColor: filtersExpanded ? `${BRAND_COLORS.primary}` : 'transparent',
            color: filtersExpanded ? 'white' : BRAND_COLORS.primary,
            '&:hover': {
              backgroundColor: filtersExpanded ? `${BRAND_COLORS.primary}DD` : `${BRAND_COLORS.primary}11`,
            }
          }}
        >
          {filtersExpanded ? "Hide Filters" : "Show Filters"}
        </Button>
      </Box>

      <Typography variant="body1" paragraph>
        Here are the best packaging options for your products. Solutions are sorted by
        <strong> minimum number of boxes needed first</strong>, then by <strong>highest space utilization</strong> to
        minimize packaging costs and waste.
      </Typography>

      {/* Filters */}
      <Collapse in={filtersExpanded}>
        <Paper sx={{ mb: 3, p: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Filter Solutions
          </Typography>
          
          <Box sx={{ mb: 3, px: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontStyle: 'italic' }}>
              <InfoOutlinedIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-top' }} />
              The "Best option" is the solution that requires the fewest boxes while maximizing space utilization.
              Solutions labeled "Min. boxes" also achieve the minimum box count but with different space efficiency.
            </Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography id="max-boxes-slider" gutterBottom>
                Maximum Number of Boxes: {maxBoxesFilter}
              </Typography>
              <Slider
                aria-labelledby="max-boxes-slider"
                value={maxBoxesFilter}
                onChange={(_, value) => setMaxBoxesFilter(value as number)}
                min={1}
                max={Math.max(20, Math.max(...solutions.map(s => s.totalBoxesNeeded)) + 5)}
                valueLabelDisplay="auto"
                marks={[
                  { value: 1, label: '1' },
                  { value: Math.min(5, Math.max(...solutions.map(s => s.totalBoxesNeeded))), label: `${Math.min(5, Math.max(...solutions.map(s => s.totalBoxesNeeded)))}` },
                  { value: Math.max(10, Math.max(...solutions.map(s => s.totalBoxesNeeded))), label: `${Math.max(10, Math.max(...solutions.map(s => s.totalBoxesNeeded)))}` }
                ]}
                sx={{ color: BRAND_COLORS.primary }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography id="min-efficiency-slider" gutterBottom>
                Minimum Space Utilization: {minEfficiencyFilter}%
              </Typography>
              <Slider
                aria-labelledby="min-efficiency-slider"
                value={minEfficiencyFilter}
                onChange={(_, value) => setMinEfficiencyFilter(value as number)}
                min={0}
                max={100}
                step={5}
                valueLabelDisplay="auto"
                marks={[
                  { value: 0, label: '0%' },
                  { value: 50, label: '50%' },
                  { value: 100, label: '100%' }
                ]}
                sx={{ color: BRAND_COLORS.secondary }}
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredSolutions.length} of {solutions.length} solutions
            </Typography>
            
            <Button 
              size="small" 
              variant="outlined" 
              onClick={() => {
                setMaxBoxesFilter(Math.max(...solutions.map(s => s.totalBoxesNeeded)) + 5);
                setMinEfficiencyFilter(0);
              }}
            >
              Reset Filters
            </Button>
          </Box>
        </Paper>
      </Collapse>

      {/* Density rail — rank all candidates by fill height at a glance */}
      <DensityRail values={filteredSolutions.map((s) => calculateActualSpaceUtilization(s))} bestIndex={0} />

      {filteredSolutions.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            mb: 4,
            backgroundColor: BRAND_COLORS.cardStock,
            borderLeft: `4px solid ${BRAND_COLORS.amber}`,
          }}
        >
          <Typography variant="h6" color="warning.dark" gutterBottom>
            No solutions match your filters
          </Typography>
          <Typography variant="body1">
            Try adjusting your filter criteria to see more results.
          </Typography>
          <Button 
            variant="outlined" 
            color="warning" 
            sx={{ mt: 2 }}
            onClick={() => {
              setMaxBoxesFilter(Math.max(...solutions.map(s => s.totalBoxesNeeded)) + 5);
              setMinEfficiencyFilter(0);
            }}
          >
            Reset Filters
          </Button>
        </Paper>
      ) : (
        filteredSolutions.map((solution, index) => {
          const util = calculateActualSpaceUtilization(solution);
          const isPrimary = index === 0;
          const isExpanded = expandedSolution === solution.boxId;
          const similar =
            index > 0 &&
            Math.abs(util - calculateActualSpaceUtilization(filteredSolutions[0])) < 5;
          const orientationColor = getOrientationColor(solution.orientation);
          return (
        <Card
          key={solution.boxId}
          sx={{
            mb: 2,
            position: 'relative',
            overflow: 'hidden',
            borderWidth: isPrimary ? 2 : 1.5,
            transition: 'box-shadow 0.2s ease',
            ':hover': { boxShadow: '0 3px 12px rgba(23,34,46,0.16)' },
          }}
        >
          {isPrimary && <Box sx={{ height: 6, bgcolor: BRAND_COLORS.dockBlue }} />}
          {isPrimary && (
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                right: 14,
                zIndex: 2,
                px: 1.25,
                py: 0.5,
                bgcolor: BRAND_COLORS.kraft,
                color: '#3a2a12',
                borderRadius: '3px',
                boxShadow: 'inset 0 0 0 1px rgba(58,42,18,0.25)',
                fontFamily: FONTS.display,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontSize: 12,
              }}
            >
              <Box component="span" sx={{ fontFamily: FONTS.mono }}>
                #01
              </Box>{' '}
              Best
            </Box>
          )}

          <CardContent sx={{ p: 0 }}>
            <Box
              sx={{ cursor: 'pointer', p: { xs: 2, sm: 2.5 } }}
              onClick={() => handleExpandSolution(solution.boxId)}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Checkbox
                  checked={selectedSolutions[solution.boxId] || false}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSelectSolution(solution.boxId);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ p: 0.5, mt: 0.25 }}
                />
                <Box sx={{ flexShrink: 0, mt: 0.25, lineHeight: 0 }}>
                  <FillCube utilization={util} size={isPrimary ? 46 : 36} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, pr: isPrimary ? 8 : 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
                    {similar && (
                      <Chip
                        label="Similar utilization"
                        size="small"
                        sx={{
                          bgcolor: BRAND_COLORS.dockBlue,
                          color: '#fff',
                          fontFamily: FONTS.display,
                          fontWeight: 600,
                          letterSpacing: '0.04em',
                        }}
                      />
                    )}
                    <Typography variant="h6" component="div" sx={{ color: BRAND_COLORS.ink, lineHeight: 1.2 }}>
                      {solution.boxName}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={getOrientationLabel(solution.orientation)}
                      size="small"
                      variant="outlined"
                      sx={{ borderColor: orientationColor, color: BRAND_COLORS.ink, bgcolor: `${orientationColor}14`, fontFamily: FONTS.mono, fontSize: 11 }}
                    />
                    <Chip
                      label={`${solution.totalBoxesNeeded} box${solution.totalBoxesNeeded > 1 ? 'es' : ''}`}
                      size="small"
                      variant="outlined"
                      sx={{ borderColor: 'rgba(23,34,46,0.25)', color: BRAND_COLORS.ink, fontFamily: FONTS.mono, fontSize: 11 }}
                    />
                  </Box>
                </Box>
                <IconButton size="small" aria-label={isExpanded ? 'Collapse' : 'Expand'}>
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              {/* Load-line gauge: full readout for the best, compact for the rest */}
              <Box sx={{ mt: 2, pl: { xs: 0, sm: '52px' } }}>
                <FillLine utilization={util} variant={isPrimary ? 'primary' : 'compact'} />
              </Box>
            </Box>

            <Collapse in={expandedSolution === solution.boxId}>
              <Divider />
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="overline" component="div" gutterBottom sx={{ color: BRAND_COLORS.slateText }}>
                        Solution details
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: BRAND_COLORS.cardStock }}>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                                Space Utilization
                              </TableCell>
                              <TableCell>
                                {calculateActualSpaceUtilization(solution).toFixed(2)}% 
                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                  (with your requested quantity)
                                </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                                Total Boxes Needed
                              </TableCell>
                              <TableCell>
                                {solution.totalBoxesNeeded}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                              Orientation
                            </TableCell>
                            <TableCell>{getOrientationLabel(solution.orientation)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="overline" component="div" gutterBottom sx={{ color: BRAND_COLORS.slateText }}>
                        Visualization
                      </Typography>
                      {expandedSolution === solution.boxId && (
                        <BoxVisualization 
                          product={solution.products ?? productProp} 
                          solution={solution} 
                          unitType={products[0].unit === 'in' ? 'inch' : 'cm'} 
                        />
                      )}
                    </Grid>
                </Grid>
              </Box>
            </Collapse>
          </CardContent>
        </Card>
          );
        })
      )}

      {/* Partage */}
      {filteredSolutions.length > 0 && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2, backgroundColor: '#EDF7ED', border: `1px solid ${BRAND_COLORS.secondary}40` }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: BRAND_COLORS.secondary }}>
              Share solutions with your client
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Select the solutions you wish to share with your client using the checkboxes to the left of each option.
              Then click "Share Solutions" to generate a link you can send to your client.
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
              The share link will allow the client to view only the selected solutions, without access to search
              or box management features.
            </Typography>
          </Paper>

          <Button
            startIcon={<ShareIcon />}
            onClick={generateShareLink}
            variant="contained"
            color="primary"
            sx={{
              backgroundColor: BRAND_COLORS.primary,
              '&:hover': {
                backgroundColor: `${BRAND_COLORS.primary}DD`,
              }
            }}
          >
            Share Solutions
          </Button>
        </Box>
      )}

      {/* Snackbar for share link */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity="warning">
          Please select at least one solution to share.
        </Alert>
      </Snackbar>

      {/* Share Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={handleCloseShareDialog}
        aria-labelledby="share-dialog-title"
        aria-describedby="share-dialog-description"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="share-dialog-title" sx={{ bgcolor: BRAND_COLORS.primary, color: 'white' }}>
          Share Selected Solutions
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText id="share-dialog-description" paragraph>
            The following solutions have been selected for sharing. You can add a price for each solution (optional):
          </DialogContentText>
          
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: `${BRAND_COLORS.primary}11` }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Box</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Boxes needed</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Utilization</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Price (optional)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {solutions
                  .filter(s => selectedSolutions[s.boxId])
                  .map((solution, idx) => (
                    <TableRow key={solution.boxId}>
                      <TableCell>{solution.boxName}</TableCell>
                      <TableCell>{solution.totalBoxesNeeded} box(es)</TableCell>
                      <TableCell>{calculateActualSpaceUtilization(solution).toFixed(1)}%</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="e.g. 12.50 $"
                          value={boxPrices[solution.boxId] || ''}
                          onChange={(e) => handlePriceChange(solution.boxId, e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary' }}>
                                Price:
                              </Typography>
                            )
                          }}
                        />
                      </TableCell>
                    </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <DialogContentText sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            Copy this link and share it with your client. They'll be able to view the selected solutions
            without accessing other app features.
            {linkJustUpdated && (
              <Chip 
                label="Link updated" 
                color="success" 
                size="small" 
                sx={{ 
                  ml: 2, 
                  animation: 'fadeOut 1.5s forwards',
                  '@keyframes fadeOut': {
                    '0%': { opacity: 1 },
                    '100%': { opacity: 0 }
                  }
                }} 
              />
            )}
          </DialogContentText>
          
          <TextField
            autoFocus
            margin="dense"
            id="share-link"
            label="Share link"
            type="text"
            fullWidth
            value={shareLink}
            sx={{
              '& .MuiOutlinedInput-root': {
                transition: 'all 0.3s',
                ...(linkJustUpdated && {
                  borderColor: BRAND_COLORS.secondary,
                  backgroundColor: '#F1F8E9',
                }),
              }
            }}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <Tooltip
                  title={showShareTooltip ? 'Link copied!' : 'Copy to clipboard'}
                  arrow
                >
                  <IconButton
                    edge="end"
                    onClick={copyShareLink}
                    color="primary"
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              ),
            }}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
            This link is automatically updated when you modify a price
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseShareDialog} color="inherit">
            Close
          </Button>
          <Button 
            onClick={copyShareLink} 
            color="primary"
            variant="contained"
            startIcon={<ContentCopyIcon />}
          >
            Copy Link
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResultsDisplay; 